import { useRef, type CSSProperties } from "react";
import { motion } from "framer-motion";
import { CornerDownLeft, Mail, Paperclip, Phone } from "lucide-react";
import { AgeBadge } from "@/components/AgeBadge";
import { formatEUR } from "@/lib/format";
import { DEMO_CLIENTS, DEMO_SENDER } from "../demo/demoData";
import { Stamp } from "../art/Stamp";
import { TromboneClip } from "../art/TromboneClip";
import { useDossierParallax } from "../motion/useDossierParallax";

const CLIENT = DEMO_CLIENTS[0];
const ATTACHMENTS = ["F-2026-104.pdf", "F-2026-117.pdf", "F-2026-131.pdf"];

/** Point de départ et délai d'entrée d'une pièce (styles/motion.css). */
const enter = (delay: number, from: { x?: number; y?: number; r?: number } = {}) =>
  ({
    "--enter-delay": `${delay}ms`,
    ...(from.x !== undefined && { "--from-x": `${from.x}px` }),
    ...(from.y !== undefined && { "--from-y": `${from.y}px` }),
    ...(from.r !== undefined && { "--from-r": `${from.r}deg` }),
  }) as CSSProperties;

/**
 * Fiche stable et lisible ; les deux feuilles et le trombone signent la
 * marque. Au chargement, le dossier se pose pièce par pièce ; ensuite, les
 * fiches épinglées suivent la souris et le défilement (useDossierParallax),
 * et le trombone s'incline au survol, comme sur le logo.
 */
export function DossierScene() {
  const ref = useRef<HTMLElement>(null);
  const layers = useDossierParallax(ref);

  return (
    <figure ref={ref} className="group/dossier relative mx-auto w-full max-w-[540px] pt-8">
      <motion.div className="relative" style={{ ...layers.tilt, transformPerspective: 1100 }}>
        <motion.div data-layer aria-hidden="true" className="absolute inset-0" style={layers.back}>
          <div
            className="enc-sheet-in absolute inset-0 -rotate-2 translate-y-2 rounded-md bg-logo-sheet-back"
            style={enter(60, { x: -22, y: 20, r: -4 })}
          />
        </motion.div>
        <motion.div data-layer aria-hidden="true" className="absolute inset-0" style={layers.front}>
          <div
            className="enc-sheet-in absolute inset-0 -rotate-1 translate-x-1 translate-y-1 rounded-md bg-logo-sheet-front"
            style={enter(140, { x: 14, y: 16, r: 3 })}
          />
        </motion.div>

        <motion.div className="relative" style={layers.card}>
          <article
            className="enc-sheet-in relative rounded-md border border-border bg-surface p-5 shadow-card sm:p-7"
            style={enter(220, { y: 14 })}
          >
            <p className="text-caption font-semibold uppercase tracking-[0.14em] text-subtle-foreground">Fiche client</p>
            <h3 className="mt-1.5 text-xl font-semibold tracking-tight text-foreground">{CLIENT.name}</h3>
            <p className="mt-0.5 text-ui text-muted-foreground [overflow-wrap:anywhere]">{CLIENT.invoices} factures échues · {CLIENT.contact}</p>

            <div className="mt-5 border-b border-border pb-4">
              <div className="flex flex-wrap items-center gap-3">
                <span className="text-[34px] leading-none font-semibold tracking-tight text-foreground">{formatEUR(CLIENT.amount)}</span>
                <AgeBadge days={CLIENT.days} />
              </div>
              <div className="mt-4">
                {/* Le statut se pose une fois le trombone en place. */}
                <span className="enc-settle inline-flex" style={{ animationDelay: "900ms" }}>
                  <Stamp label="Paiement annoncé" date="02/10" />
                </span>
              </div>
            </div>

            <ol className="mt-4 space-y-3.5 text-ui">
              <li className="flex gap-3">
                <Phone aria-hidden className="mt-0.5 size-4 shrink-0 text-action" />
                <div>
                  <p className="font-semibold text-foreground">Appel · 24/09</p>
                  <p className="text-muted-foreground">Règlement annoncé pour le vendredi 2 octobre.</p>
                  <p className="mt-1 text-caption text-subtle-foreground">Appel consigné par Claire.</p>
                </div>
              </li>
              <li className="flex gap-3">
                <CornerDownLeft aria-hidden className="mt-0.5 size-4 shrink-0 text-action" />
                <div>
                  <p className="font-semibold text-foreground">Réponse reçue · 19/09</p>
                  <p className="text-muted-foreground">Rangée dans la fiche, avec le reste.</p>
                </div>
              </li>
              <li className="flex gap-3">
                <Mail aria-hidden className="mt-0.5 size-4 shrink-0 text-action" />
                <div>
                  <p className="font-semibold text-foreground">Relance amiable · 18/09</p>
                  <p className="text-muted-foreground">Partie de votre Gmail, les 3 factures en PDF jointes.</p>
                </div>
              </li>
            </ol>
          </article>
        </motion.div>

        <motion.div
          data-layer
          aria-hidden="true"
          className="absolute -top-8 right-8 text-primary sm:right-10"
          style={layers.clip}
        >
          <div className="enc-clip-in" style={enter(560)}>
            <div className="enc-clip-tilt">
              <TromboneClip height={76} />
            </div>
          </div>
        </motion.div>
      </motion.div>

      <motion.div className="relative mt-5" style={layers.mail}>
        <div
          className="enc-sheet-in rounded-card border border-border bg-surface px-4 py-3 text-caption"
          style={enter(400, { y: 12 })}
        >
          <p className="truncate text-muted-foreground"><span className="font-semibold text-foreground">De&nbsp;:</span> {DEMO_SENDER.email}</p>
          <p className="mt-0.5 truncate text-muted-foreground"><span className="font-semibold text-foreground">Objet&nbsp;:</span> Relance — {CLIENT.name} — {formatEUR(CLIENT.amount)} en attente</p>
          <ul className="mt-2 flex flex-wrap gap-1.5">
            {ATTACHMENTS.map((file) => (
              <li key={file} className="inline-flex items-center gap-1 rounded-md bg-muted px-2 py-1 font-mono text-2xs text-muted-foreground">
                <Paperclip aria-hidden className="size-3" />{file}
              </li>
            ))}
          </ul>
        </div>
      </motion.div>
      <figcaption className="mt-3 text-right text-caption text-subtle-foreground">Données fictives</figcaption>
    </figure>
  );
}
